import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  Grid,
  Divider,
} from '@mui/material';
import { ArrowBack, AddCircle, Delete, Save } from '@mui/icons-material';
import { PageHeader, headerCellSx } from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import { procurementService, Vendor } from '../../services/procurementService';
import { projectService, Project } from '../../services/projectService';
import { SwoStatus } from './ServiceWorkOrderPage';

interface CostCenter {
  id: number;
  code: string;
  name: string;
  isActive?: boolean;
}

const toNum = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

let draftKeySeq = 0;
const nextDraftKey = () => `swoline-${++draftKeySeq}`;

interface LineDraft {
  key: string;
  description: string;
  quantity: string;
  uom: string;
  rate: string;
  sacCode: string;
  costCenterId: string;
}

const emptyLine = (): LineDraft => ({
  key: nextDraftKey(),
  description: '',
  quantity: '1',
  uom: '',
  rate: '',
  sacCode: '',
  costCenterId: '',
});

interface SwoDetailResponse {
  id: number;
  vendor_id: number | string;
  vendor_name?: string;
  service_category?: string;
  plant?: string;
  department?: string;
  cost_center_id?: number | string | null;
  project_id?: number | string | null;
  wo_date?: string;
  start_date?: string | null;
  end_date?: string | null;
  scope_of_work?: string;
  payment_terms?: string;
  contract_reference?: string;
  sla?: string;
  warranty?: string;
  remarks?: string;
  tax_amount?: number | string;
  status: SwoStatus;
  items?: Array<{
    id?: number;
    description: string;
    quantity: number | string;
    uom?: string;
    rate: number | string;
    sac_code?: string;
    cost_center_id?: number | string | null;
  }>;
}

const ServiceWorkOrderFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingRefData, setLoadingRefData] = useState(true);
  const [loadingOrder, setLoadingOrder] = useState(isEdit);

  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [serviceCategory, setServiceCategory] = useState('');
  const [plant, setPlant] = useState('');
  const [department, setDepartment] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [woDate, setWoDate] = useState(todayISO());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [scopeOfWork, setScopeOfWork] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [contractReference, setContractReference] = useState('');
  const [sla, setSla] = useState('');
  const [warranty, setWarranty] = useState('');
  const [remarks, setRemarks] = useState('');
  const [taxAmount, setTaxAmount] = useState('0');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [status, setStatus] = useState<SwoStatus | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    setLoadingRefData(true);
    Promise.all([
      procurementService.getVendors().catch(() => ({ vendors: [] as Vendor[], count: 0 })),
      // Cost centers / projects are optional context (Finance Workspace /
      // Projects module) — a tenant without that module enabled, or the
      // endpoint being unavailable, must not block raising a work order.
      api.get<{ costCenters: CostCenter[] }>('/cost-centers').catch(() => ({ costCenters: [] as CostCenter[] })),
      projectService.getProjects().catch(() => [] as Project[]),
    ])
      .then(([vendorsRes, costCentersRes, projectsRes]) => {
        setVendors((vendorsRes.vendors || []).filter((v) => v.is_active !== false));
        setCostCenters((costCentersRes.costCenters || []).filter((c) => c.isActive !== false));
        setProjects(projectsRes || []);
      })
      .finally(() => setLoadingRefData(false));
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoadingOrder(true);
    api
      .get<{ serviceWorkOrder: SwoDetailResponse }>(`/service-work-orders/${id}`)
      .then(({ serviceWorkOrder }) => {
        setSelectedVendor({
          id: String(serviceWorkOrder.vendor_id),
          company_name: serviceWorkOrder.vendor_name || '',
        } as Vendor);
        setServiceCategory(serviceWorkOrder.service_category || '');
        setPlant(serviceWorkOrder.plant || '');
        setDepartment(serviceWorkOrder.department || '');
        setCostCenterId(serviceWorkOrder.cost_center_id ? String(serviceWorkOrder.cost_center_id) : '');
        setProjectId(serviceWorkOrder.project_id ? String(serviceWorkOrder.project_id) : '');
        setWoDate(serviceWorkOrder.wo_date ? serviceWorkOrder.wo_date.slice(0, 10) : todayISO());
        setStartDate(serviceWorkOrder.start_date ? serviceWorkOrder.start_date.slice(0, 10) : '');
        setEndDate(serviceWorkOrder.end_date ? serviceWorkOrder.end_date.slice(0, 10) : '');
        setScopeOfWork(serviceWorkOrder.scope_of_work || '');
        setPaymentTerms(serviceWorkOrder.payment_terms || '');
        setContractReference(serviceWorkOrder.contract_reference || '');
        setSla(serviceWorkOrder.sla || '');
        setWarranty(serviceWorkOrder.warranty || '');
        setRemarks(serviceWorkOrder.remarks || '');
        setTaxAmount(String(toNum(serviceWorkOrder.tax_amount) || 0));
        setStatus(serviceWorkOrder.status);
        setLines(
          (serviceWorkOrder.items || []).map((it) => ({
            key: nextDraftKey(),
            description: it.description || '',
            quantity: String(it.quantity ?? ''),
            uom: it.uom || '',
            rate: String(it.rate ?? ''),
            sacCode: it.sac_code || '',
            costCenterId: it.cost_center_id ? String(it.cost_center_id) : '',
          }))
        );
      })
      .catch((err: any) => setError(err.message || 'Failed to load service work order'))
      .finally(() => setLoadingOrder(false));
  }, [isEdit, id]);

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (key: string) => setLines((prev) => prev.filter((l) => l.key !== key));
  const updateLine = (key: string, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const estimatedValue = useMemo(
    () => lines.reduce((sum, l) => sum + toNum(l.quantity) * toNum(l.rate), 0),
    [lines]
  );
  const total = useMemo(() => estimatedValue + toNum(taxAmount), [estimatedValue, taxAmount]);

  const canEdit = !isEdit || status === 'DRAFT';

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!selectedVendor) errs.push('A vendor is required.');
    if (!serviceCategory.trim()) errs.push('Service category is required.');
    if (!woDate) errs.push('Work order date is required.');
    if (lines.length === 0) errs.push('At least one scope line is required.');
    lines.forEach((l, idx) => {
      if (!l.description.trim()) errs.push(`Line ${idx + 1}: description is required.`);
      if (toNum(l.quantity) <= 0) errs.push(`Line ${idx + 1}: quantity must be greater than 0.`);
      if (toNum(l.rate) < 0) errs.push(`Line ${idx + 1}: rate cannot be negative.`);
    });
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setValidationErrors(errs);
    if (errs.length > 0 || !selectedVendor) return;

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        vendorId: selectedVendor.id,
        serviceCategory: serviceCategory.trim(),
        plant: plant.trim() || undefined,
        department: department.trim() || undefined,
        costCenterId: costCenterId ? Number(costCenterId) : undefined,
        projectId: projectId ? Number(projectId) : undefined,
        woDate,
        scopeOfWork: scopeOfWork.trim() || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        taxAmount: toNum(taxAmount),
        paymentTerms: paymentTerms.trim() || undefined,
        contractReference: contractReference.trim() || undefined,
        sla: sla.trim() || undefined,
        warranty: warranty.trim() || undefined,
        remarks: remarks.trim() || undefined,
        items: lines.map((l) => ({
          description: l.description.trim(),
          quantity: toNum(l.quantity),
          uom: l.uom.trim() || undefined,
          rate: toNum(l.rate),
          sacCode: l.sacCode.trim() || undefined,
          costCenterId: l.costCenterId ? Number(l.costCenterId) : undefined,
        })),
      };

      const { serviceWorkOrder } =
        isEdit && id
          ? await api.put<{ serviceWorkOrder: { id: number } }>(`/service-work-orders/${id}`, payload)
          : await api.post<{ serviceWorkOrder: { id: number } }>('/service-work-orders', payload);

      navigate(`/procurement/service-work-orders/${serviceWorkOrder.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to save service work order');
    } finally {
      setSubmitting(false);
    }
  };

  const loading = loadingRefData || loadingOrder;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() =>
          navigate(isEdit && id ? `/procurement/service-work-orders/${id}` : '/procurement/service-work-orders')
        }
        sx={{ mb: 1 }}
      >
        Back
      </Button>

      <PageHeader
        title={isEdit ? 'Edit Service Work Order' : 'New Service Work Order'}
        subtitle="Commission an outside service before it can be entered and billed"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!canEdit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This service work order is {status?.replace('_', ' ')} and can no longer be edited.
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((e) => (
            <Box key={e}>{e}</Box>
          ))}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Work Order Details
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Autocomplete
              options={vendors}
              size="small"
              value={selectedVendor}
              onChange={(_, v) => setSelectedVendor(v)}
              getOptionLabel={(o) => o.company_name || ''}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
              disabled={!canEdit}
              renderInput={(params) => <TextField {...params} label="Vendor *" required />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Service Category *"
              value={serviceCategory}
              onChange={(e) => setServiceCategory(e.target.value)}
              disabled={!canEdit}
              placeholder="e.g. AMC, Repairs, Consulting"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="WO Date *"
              value={woDate}
              onChange={(e) => setWoDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Plant"
              value={plant}
              onChange={(e) => setPlant(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Department"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Cost Center"
              value={costCenterId}
              onChange={(e) => setCostCenterId(e.target.value)}
              disabled={!canEdit}
            >
              <MenuItem value="">Not assigned</MenuItem>
              {costCenters.length === 0 ? (
                <MenuItem value="" disabled>
                  No cost centers configured
                </MenuItem>
              ) : (
                costCenters.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.code} — {c.name}
                  </MenuItem>
                ))
              )}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Project"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              disabled={!canEdit}
            >
              <MenuItem value="">Not assigned</MenuItem>
              {projects.length === 0 ? (
                <MenuItem value="" disabled>
                  No projects configured
                </MenuItem>
              ) : (
                projects.map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>
                    {p.project_code} — {p.project_name}
                  </MenuItem>
                ))
              )}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Scope of Work"
              value={scopeOfWork}
              onChange={(e) => setScopeOfWork(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Payment Terms"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              disabled={!canEdit}
              placeholder="e.g. Net 30"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Contract Reference"
              value={contractReference}
              onChange={(e) => setContractReference(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="SLA"
              value={sla}
              onChange={(e) => setSla(e.target.value)}
              disabled={!canEdit}
              placeholder="e.g. 4-hour response"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Warranty"
              value={warranty}
              onChange={(e) => setWarranty(e.target.value)}
              disabled={!canEdit}
              placeholder="e.g. 12 months"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', mb: 2.5 }}>
        <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Scope Lines
          </Typography>
          {canEdit && (
            <Button size="small" variant="outlined" startIcon={<AddCircle />} onClick={addLine}>
              Add Line
            </Button>
          )}
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Description</TableCell>
                <TableCell sx={headerCellSx} align="right">Qty</TableCell>
                <TableCell sx={headerCellSx}>UOM</TableCell>
                <TableCell sx={headerCellSx} align="right">Rate</TableCell>
                <TableCell sx={headerCellSx}>SAC Code</TableCell>
                <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                {canEdit && <TableCell sx={headerCellSx} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l) => (
                <TableRow key={l.key} hover>
                  <TableCell sx={{ minWidth: 220 }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={l.description}
                      onChange={(e) => updateLine(l.key, { description: e.target.value })}
                      disabled={!canEdit}
                      placeholder="Scope description"
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 100 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={l.quantity}
                      onChange={(e) => updateLine(l.key, { quantity: e.target.value })}
                      inputProps={{ min: 0, step: '0.01' }}
                      disabled={!canEdit}
                      sx={{ minWidth: 90 }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 90 }}>
                    <TextField
                      size="small"
                      value={l.uom}
                      onChange={(e) => updateLine(l.key, { uom: e.target.value })}
                      disabled={!canEdit}
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 120 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={l.rate}
                      onChange={(e) => updateLine(l.key, { rate: e.target.value })}
                      inputProps={{ min: 0, step: '0.01' }}
                      disabled={!canEdit}
                      sx={{ minWidth: 110 }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 110 }}>
                    <TextField
                      size="small"
                      value={l.sacCode}
                      onChange={(e) => updateLine(l.key, { sacCode: e.target.value })}
                      disabled={!canEdit}
                      sx={{ minWidth: 100 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {formatIndianNumber(toNum(l.quantity) * toNum(l.rate))}
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => removeLine(l.key)} disabled={lines.length === 1}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={1} justifyContent="flex-end">
            <Grid size={{ xs: 12, sm: 5, md: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Estimated Value</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatIndianNumber(estimatedValue)}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 5, md: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Tax Amount</Typography>
                <TextField
                  size="small"
                  type="number"
                  value={taxAmount}
                  onChange={(e) => setTaxAmount(e.target.value)}
                  inputProps={{ min: 0, step: '0.01' }}
                  disabled={!canEdit}
                  sx={{ width: 130 }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 10, md: 6 }}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Total: {formatIndianNumber(total)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button
            onClick={() =>
              navigate(isEdit && id ? `/procurement/service-work-orders/${id}` : '/procurement/service-work-orders')
            }
          >
            Cancel
          </Button>
          <Button variant="contained" startIcon={<Save />} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Work Order'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ServiceWorkOrderFormPage;
